import React, { useState, useEffect } from 'react';
import { independentQuotesService, independentItinerariesService, independentClientsService } from '../../services/independent';
import { TrendingUp, DollarSign, Users, FileText, Calendar, PieChart, BarChart3, Download } from 'lucide-react';

// Voyager Pro reskin - restyled to Tailwind/CSS-var to match AgentAnalytics.jsx's
// visual language. Left as its own implementation rather than merged with
// AgentAnalytics.jsx: this component computes metrics client-side from the
// unified quotes/itineraries/clients services (which are correct/current -
// see memory entity "TPCRM Voyager Pro CRM Reskin Project"), whereas
// AgentAnalytics.jsx reads a differently-shaped `dashboardData` object from
// analyticsService.getDashboard() - unifying the two data shapes was judged
// too risky to do in the same pass as everything else this session.
// Dropped an unused `independentAnalyticsService` import that was never
// actually called here (it IS used by IndependentDashboard.jsx, where a
// real bug in backend/routes/independent/analytics.js - reading from dead
// independent_quotes/independent_itineraries tables - was fixed separately).

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-2xl border border-[var(--border)] p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-[var(--muted-foreground)]">{label}</p>
          <p className="text-2xl font-bold text-[var(--foreground)] mt-1">{value}</p>
          {sub && <p className="text-xs text-[var(--muted-foreground)] mt-1">{sub}</p>}
        </div>
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color }}>
          <Icon className="text-white" size={20} />
        </div>
      </div>
    </div>
  );
}

function RevenueCard({ label, value, sub }) {
  return (
    <div className="rounded-2xl p-5 text-white" style={{ background: 'linear-gradient(135deg, var(--primary), var(--accent))' }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/80 text-sm">{label}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          <p className="text-white/70 text-xs mt-1">{sub}</p>
        </div>
        <DollarSign size={30} className="text-white/60" />
      </div>
    </div>
  );
}

function StatusRow({ color, label, value }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
        <span className="text-sm text-[var(--muted-foreground)]">{label}</span>
      </div>
      <span className="text-sm font-semibold text-[var(--foreground)]">{value}</span>
    </div>
  );
}

const IndependentAnalytics = () => {
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line
  }, [dateRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const [quotes, itineraries, clients] = await Promise.all([
        independentQuotesService.getAll(),
        independentItinerariesService.getAll(),
        independentClientsService.getAll(),
      ]);

      const quotesData = Array.isArray(quotes) ? quotes : quotes.data || [];
      const itinerariesData = Array.isArray(itineraries) ? itineraries : itineraries.data || [];
      const clientsData = Array.isArray(clients) ? clients : clients.data || [];

      const now = new Date();
      const daysAgo = parseInt(dateRange);
      const filterDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);

      const recentQuotes = quotesData.filter(q => new Date(q.created_at) >= filterDate);
      const recentItineraries = itinerariesData.filter(i => new Date(i.created_at) >= filterDate);
      const recentClients = clientsData.filter(c => new Date(c.created_at) >= filterDate);

      const totalQuoteValue = recentQuotes.reduce((sum, q) => sum + parseFloat(q.total_price || 0), 0);
      const totalItineraryValue = recentItineraries.reduce((sum, i) => sum + parseFloat(i.total_price || 0), 0);
      const totalRevenue = recentItineraries.filter(i => i.status === 'completed').reduce((sum, i) => sum + parseFloat(i.total_price || 0), 0);
      const conversionRate = recentQuotes.length > 0 ? (recentItineraries.length / recentQuotes.length * 100).toFixed(1) : 0;

      setAnalytics({
        totalQuotes: recentQuotes.length,
        totalItineraries: recentItineraries.length,
        totalClients: recentClients.length,
        totalQuoteValue,
        totalItineraryValue,
        totalRevenue,
        conversionRate,
        avgQuoteValue: recentQuotes.length > 0 ? (totalQuoteValue / recentQuotes.length).toFixed(2) : 0,
        avgItineraryValue: recentItineraries.length > 0 ? (totalItineraryValue / recentItineraries.length).toFixed(2) : 0,
        quotesByStatus: {
          pending: recentQuotes.filter(q => q.status === 'pending').length,
          sent: recentQuotes.filter(q => q.status === 'sent').length,
          accepted: recentQuotes.filter(q => q.status === 'accepted').length,
          declined: recentQuotes.filter(q => q.status === 'declined').length,
        },
        itinerariesByStatus: {
          confirmed: recentItineraries.filter(i => i.status === 'confirmed').length,
          pending: recentItineraries.filter(i => i.status === 'pending').length,
          completed: recentItineraries.filter(i => i.status === 'completed').length,
          cancelled: recentItineraries.filter(i => i.status === 'cancelled').length,
        },
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    const report = [
      ['Independent Agent Analytics Report'],
      ['Generated:', new Date().toLocaleString()],
      ['Date Range:', `Last ${dateRange} days`],
      [''],
      ['Key Metrics'],
      ['Total Quotes', analytics.totalQuotes],
      ['Total Itineraries', analytics.totalItineraries],
      ['Total Clients', analytics.totalClients],
      ['Quote Value', `$${analytics.totalQuoteValue?.toLocaleString()}`],
      ['Itinerary Value', `$${analytics.totalItineraryValue?.toLocaleString()}`],
      ['Revenue', `$${analytics.totalRevenue?.toLocaleString()}`],
      ['Conversion Rate', `${analytics.conversionRate}%`],
      ['Avg Quote Value', `$${analytics.avgQuoteValue}`],
      ['Avg Itinerary Value', `$${analytics.avgItineraryValue}`],
      [''],
      ['Quote Status Breakdown'],
      ['Pending', analytics.quotesByStatus?.pending],
      ['Sent', analytics.quotesByStatus?.sent],
      ['Accepted', analytics.quotesByStatus?.accepted],
      ['Declined', analytics.quotesByStatus?.declined],
      [''],
      ['Itinerary Status Breakdown'],
      ['Confirmed', analytics.itinerariesByStatus?.confirmed],
      ['Pending', analytics.itinerariesByStatus?.pending],
      ['Completed', analytics.itinerariesByStatus?.completed],
      ['Cancelled', analytics.itinerariesByStatus?.cancelled],
    ];

    const csv = report.map(row => row.map(cell => `"${cell ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="voyager-app flex items-center justify-center min-h-[400px] text-sm text-[var(--muted-foreground)]">Loading analytics…</div>;
  }

  return (
    <div className="voyager-app p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl text-[var(--foreground)] mb-1">Analytics Dashboard</h1>
            <p className="text-[var(--muted-foreground)] text-sm">Track your business performance and metrics.</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={dateRange} onChange={e => setDateRange(e.target.value)} className="px-4 py-2.5 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none cursor-pointer">
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
            <button onClick={exportReport} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[var(--border)] bg-white text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/50 transition-colors">
              <Download size={16} />
              Export Report
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard icon={FileText} label="Total Quotes" value={analytics.totalQuotes} sub={`Last ${dateRange} days`} color="var(--primary)" />
          <StatCard icon={Calendar} label="Total Itineraries" value={analytics.totalItineraries} sub={`Last ${dateRange} days`} color="var(--accent)" />
          <StatCard icon={Users} label="New Clients" value={analytics.totalClients} sub={`Last ${dateRange} days`} color="#7C3AED" />
          <StatCard icon={TrendingUp} label="Conversion Rate" value={`${analytics.conversionRate}%`} sub="Quotes to Itineraries" color="#D97706" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <RevenueCard label="Total Quote Value" value={`$${analytics.totalQuoteValue?.toLocaleString()}`} sub={`Avg: $${analytics.avgQuoteValue}`} />
          <RevenueCard label="Total Itinerary Value" value={`$${analytics.totalItineraryValue?.toLocaleString()}`} sub={`Avg: $${analytics.avgItineraryValue}`} />
          <RevenueCard label="Total Revenue" value={`$${analytics.totalRevenue?.toLocaleString()}`} sub="Completed trips" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-[var(--border)] p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-[var(--foreground)] uppercase tracking-widest">Quote Status</h2>
              <PieChart className="text-[var(--muted-foreground)]" size={18} />
            </div>
            <div className="space-y-3">
              <StatusRow color="#EAB308" label="Pending" value={analytics.quotesByStatus?.pending || 0} />
              <StatusRow color="#3B82F6" label="Sent" value={analytics.quotesByStatus?.sent || 0} />
              <StatusRow color="#22C55E" label="Accepted" value={analytics.quotesByStatus?.accepted || 0} />
              <StatusRow color="#EF4444" label="Declined" value={analytics.quotesByStatus?.declined || 0} />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[var(--border)] p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-[var(--foreground)] uppercase tracking-widest">Itinerary Status</h2>
              <BarChart3 className="text-[var(--muted-foreground)]" size={18} />
            </div>
            <div className="space-y-3">
              <StatusRow color="#22C55E" label="Confirmed" value={analytics.itinerariesByStatus?.confirmed || 0} />
              <StatusRow color="#EAB308" label="Pending" value={analytics.itinerariesByStatus?.pending || 0} />
              <StatusRow color="#3B82F6" label="Completed" value={analytics.itinerariesByStatus?.completed || 0} />
              <StatusRow color="#EF4444" label="Cancelled" value={analytics.itinerariesByStatus?.cancelled || 0} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[var(--border)] p-5">
          <h2 className="text-sm font-bold text-[var(--foreground)] uppercase tracking-widest mb-4">Performance Insights</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--muted)' }}>
              <p className="text-sm font-medium mb-1" style={{ color: 'var(--primary)' }}>Average Quote Value</p>
              <p className="text-2xl font-bold text-[var(--foreground)]">${analytics.avgQuoteValue}</p>
              <p className="text-xs text-[var(--muted-foreground)] mt-1">Per quote created</p>
            </div>
            <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--muted)' }}>
              <p className="text-sm font-medium mb-1" style={{ color: 'var(--accent)' }}>Average Itinerary Value</p>
              <p className="text-2xl font-bold text-[var(--foreground)]">${analytics.avgItineraryValue}</p>
              <p className="text-xs text-[var(--muted-foreground)] mt-1">Per confirmed trip</p>
            </div>
            <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--muted)' }}>
              <p className="text-sm font-medium mb-1" style={{ color: '#7C3AED' }}>Quote to Itinerary</p>
              <p className="text-2xl font-bold text-[var(--foreground)]">{analytics.conversionRate}%</p>
              <p className="text-xs text-[var(--muted-foreground)] mt-1">Conversion rate</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndependentAnalytics;
